import React, { useState, useEffect, useRef } from 'react'

import { authedAxios } from '../../utils'
import { useNavigate, useParams } from 'react-router-dom'
import DocumentList from '../../common/documents/DocumentList'
import {
  Box,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  Card,
  CardContent,
} from '@mui/material'
import { Add, Description, Launch } from '@mui/icons-material'
import GenericLoadError from '../../common/GenericLoadError'
import useIsAdmin from '../../hooks/useIsAdmin'
import CustomerHeader from '../components/CustomerHeader'
import useDropdowns from '../../hooks/api/useDropdowns'
import { blue, grey } from '@mui/material/colors'
import Tooltip from '../../common/Tooltip'

const Documents = ({
  customerQuery,
  customerStatusTypesData,
  types,
  customerTicketsQuery,
}) => {
  const { id } = useParams()
  const [documents, setDocuments] = useState([])

  const [loading, setLoading] = useState(true)
  const [errorLoading, setErrorLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [hasUploadError, setHasUploadError] = useState(false)

  const [tab, setTab] = useState(0)

  const inputRef = useRef()

  const { isPriveledged } = useIsAdmin()

  const { dropdownsSummary } = useDropdowns()

  const documentCategoryDropdown = dropdownsSummary.find(
    (dd) => dd.key === 'documentCategory'
  )

  const getDocuments = () => {
    setLoading(true)
    return authedAxios
      .get(`/api/customer/${id}/documents`)
      .then((res) => {
        setLoading(false)
        setDocuments(res.data.documents)
      })
      .catch((_e) => {
        setErrorLoading(true)
      })
  }

  useEffect(() => {
    getDocuments()
  }, [])

  const getSelectedCategoryTypeId = () => {
    if (tab === 0) return null

    return documentCategoryDropdown?.options?.[tab - 1]?.id
  }

  const selectedCategoryTypeId = getSelectedCategoryTypeId()

  const displayDocuments = documents.filter(
    (d) => d.categoryTypeId === selectedCategoryTypeId
  )

  const handleSelectFile = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0])
    }
  }

  const handleFileUpload = (file) => {
    setIsUploading(true)

    const formData = new FormData()
    formData.append('file', file)

    return authedAxios
      .put(`/api/customer/${id}/document/${selectedCategoryTypeId}`, formData, {
        headers: {
          'content-type': 'multipart/form-data',
        },
      })
      .then(() => {
        setIsUploading(false)
        getDocuments()
      })
      .catch(() => {
        setHasUploadError(true)
        setIsUploading(false)
      })
      .finally(() => {
        const inputEl = document.getElementById('document-input')
        if (inputEl) inputEl.value = ''
      })
  }

  const navigate = useNavigate()

  if (loading) return <CircularProgress />
  if (errorLoading) return <GenericLoadError />

  return (
    <>
      <CustomerHeader
        customer={customerQuery.customer}
        customerStatusTypesData={customerStatusTypesData}
        customerTicketsQuery={customerTicketsQuery}
        types={types}
        Icon={Description}
        tooltip={
          <Tooltip
            infoText={
              'In this section, you can access company documents and make any needed changes. After saving the edited versions, proceed to upload them back into this designated account and arrange the document within the preferred category.'
            }
          />
        }
      />
      <Card>
        <CardContent>
          <Box sx={{ mt: 2 }}>
            {isPriveledged && (
              <Box
                sx={{
                  display: 'flex',

                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  sx={{ mr: 1 }}
                  onClick={() => {
                    navigate('/dashboard/documents')
                  }}
                  endIcon={<Launch />}
                  variant='outlined'
                >
                  View company documents
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => inputRef.current.click()}
                  endIcon={<Add />}
                >
                  Upload a document
                </Button>
              </Box>
            )}

            <input
              hidden
              type='file'
              onChange={handleSelectFile}
              ref={inputRef}
              id='document-input'
            />

            <Box sx={{ display: 'flex' }}>
              <Box sx={{ mr: 2 }}>
                <Typography sx={{ mt: 2 }}>Categories</Typography>
                <Tabs
                  orientation='vertical'
                  sx={{
                    mt: 1,
                    flexShrink: 0,
                    maxHeight: '400px',
                    backgroundColor: 'white',
                    border: `1px solid ${grey[300]}`,
                    maxWidth: '200px',
                    minWidth: '200px',
                    flexShrink: 0,
                  }}
                  value={tab}
                  onChange={(_e, val) => setTab(val)}
                  variant='scrollable'
                >
                  <Tab
                    sx={{
                      alignItems: 'start',
                      backgroundColor: tab === 0 ? blue[50] : null,
                      fontWeight: tab === 0 ? 'bold' : null,
                    }}
                    label={'Uncategorized'}
                  ></Tab>
                  {documentCategoryDropdown &&
                    documentCategoryDropdown.options.map((o, i) => {
                      return (
                        <Tab
                          sx={{
                            alignItems: 'start',
                            borderTop: `1px solid ${grey[200]}`,
                            backgroundColor: tab === i + 1 ? blue[50] : null,
                            fontWeight: tab === i + 1 ? 'bold' : null,
                          }}
                          key={o.id}
                          label={o.name}
                        ></Tab>
                      )
                    })}
                </Tabs>
              </Box>
              <Box sx={{ mt: 2 }}>
                <DocumentList
                  documentCategoryDropdown={documentCategoryDropdown}
                  documents={displayDocuments}
                  removeDocument={(document) =>
                    setDocuments((prev) =>
                      prev.filter((d) => d.s3Key !== document.s3Key)
                    )
                  }
                  setDocument={(document) => {
                    setDocuments((prev) =>
                      prev.map((d) => {
                        if (d.s3Key !== document.s3Key) return d
                        return document
                      })
                    )
                  }}
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </>
  )
}

export default Documents
